import { Blob, File as ConsigliFile } from '@consigli/types';
import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  ReactNode,
} from 'react';

import { ServiceType } from '@/utils/types';

import { useDownloadDocument } from '../hooks/use-download-document';
import { useGetDocuments } from '../hooks/use-get-documents';
import { useDeleteDocument } from '../hooks/use-handle-delete';
import { useViewDetails } from '../hooks/use-view-details';
import { useDocumentActions } from '../table/actions';

const PAGE_SIZE = 50;

interface DocumentsContextValue {
  // Data
  results: unknown[];
  isLoading: boolean;
  projectId: number;
  serviceId: string;
  serviceType: ServiceType;
  isBidReview: boolean;

  // Pagination
  page: number;
  setPage: (page: number) => void;
  totalCount: number;
  pageSize: number;
  isFetching: boolean;

  // Modal state
  isModalOpen: boolean;
  blobToDelete: Blob | null;
  findingsToDelete: number;
  isEditModalOpen: boolean;
  itemToEdit: Blob | ConsigliFile | null;
  newBaseName: string;
  isDeleting: boolean;
  isUpdating: boolean;

  // Detail view state
  selectedItem: Blob | ConsigliFile | null;
  isDetailViewOpen: boolean;

  // File preview state
  previewItem: Blob | ConsigliFile | null;
  setPreviewItem: (item: Blob | ConsigliFile | null) => void;

  // Modal handlers
  handleCloseModal: () => void;
  handleCloseEditModal: () => void;
  handleConfirmDelete: () => void;
  handleConfirmEdit: () => void;
  setNewBaseName: (name: string) => void;
  setIsDetailViewOpen: (open: boolean) => void;
  setSelectedItem: (item: Blob | ConsigliFile | null) => void;

  // Action handlers
  handleViewDetails: (item: unknown) => void;
  handleEditFilename: (item: unknown) => void;
  handleDownload: (item: unknown) => void;
  handleDelete: (item: unknown) => void;
}

interface DocumentsProviderProps {
  children: ReactNode;
  serviceId: string;
  projectId: number;
  serviceType: ServiceType;
  isBidReview?: boolean;
}

const DocumentsContext = createContext<DocumentsContextValue | undefined>(undefined);

export const useDocumentsContext = () => {
  const context = useContext(DocumentsContext);
  if (!context) {
    throw new Error('useDocumentsContext must be used within a DocumentsProvider');
  }
  return context;
};

export const DocumentsProvider = ({
  children,
  serviceId,
  projectId,
  serviceType,
  isBidReview = false,
}: DocumentsProviderProps) => {
  // Pagination state
  const [page, setPage] = useState(1);

  // State management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [blobToDelete, setBlobToDelete] = useState<Blob | null>(null);
  const [findingsToDelete, setFindingsToDelete] = useState<number>(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [blobToEdit, setBlobToEdit] = useState<Blob | null>(null);
  const [newBaseName, setNewBaseName] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [previewItem, setPreviewItem] = useState<Blob | ConsigliFile | null>(null);
  const deleteResolveRef = useRef<((value: boolean) => void) | null>(null);

  // View details state
  const {
    selectedItem,
    isDetailViewOpen,
    setSelectedItem,
    setIsDetailViewOpen,
    handleViewDetailsClick,
  } = useViewDetails();

  // Modal handlers
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setBlobToDelete(null);
    setFindingsToDelete(0);
    if (deleteResolveRef.current) {
      deleteResolveRef.current(false);
      deleteResolveRef.current = null;
    }
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setBlobToEdit(null);
    setNewBaseName('');
  }, []);

  // Wrapper function to handle type mismatch between Blob and File
  const setItemToEditWrapper = useCallback((item: Blob | ConsigliFile | null) => {
    if (item && 'name' in item) {
      setBlobToEdit(item as Blob);
    } else {
      setBlobToEdit(null);
    }
  }, []);

  const {
    handleEditFilenameClick: handleEditFilenameClickOriginal,
    handleConfirmEdit: handleConfirmEditOriginal,
    handleDownloadClick,
  } = useDocumentActions({
    projectId,
    packageId: serviceId,
    setBlobToDelete,
    setFindingsToDelete,
    setIsModalOpen,
    setIsDeleting,
    setItemToEdit: setItemToEditWrapper,
    setNewBaseName,
    setIsEditModalOpen,
    setIsUpdating,
  });

  const { handleDownload } = useDownloadDocument({
    handleDownloadClick,
  });

  const showDeleteModal = useCallback(
    (item: unknown): Promise<boolean> => {
      return new Promise((resolve) => {
        deleteResolveRef.current = resolve;
        if (item && typeof item === 'object' && 'name' in item && 'id' in item) {
          setBlobToDelete(item as Blob);
          setFindingsToDelete(0);
          setIsModalOpen(true);
        } else if (item && typeof item === 'object' && 'id' in item && 'filename' in item) {
          const fileItem = item as { filename: string; id: string };
          const mockBlob = {
            id: fileItem.id,
            name: fileItem.filename,
          } as Blob;
          setBlobToDelete(mockBlob);
          setFindingsToDelete(0);
          setIsModalOpen(true);
        } else {
          resolve(false);
          deleteResolveRef.current = null;
        }
      });
    },
    [setBlobToDelete, setFindingsToDelete, setIsModalOpen],
  );

  const deleteDocumentHook = useDeleteDocument(projectId, serviceId, serviceType, showDeleteModal);

  const handleDeleteClick = useCallback(
    (item: unknown) => {
      deleteDocumentHook(item);
    },
    [deleteDocumentHook],
  );

  const handleEditFilenameClick = useCallback(
    (item: Blob | ConsigliFile) => {
      if ('name' in item) {
        handleEditFilenameClickOriginal(item as Blob);
      }
    },
    [handleEditFilenameClickOriginal],
  );

  const handleConfirmEdit = useCallback(() => {
    handleConfirmEditOriginal(blobToEdit, newBaseName);
  }, [handleConfirmEditOriginal, blobToEdit, newBaseName]);

  const handleConfirmDelete = useCallback(() => {
    setIsModalOpen(false);
    setBlobToDelete(null);
    setFindingsToDelete(0);
    if (deleteResolveRef.current) {
      deleteResolveRef.current(true);
      deleteResolveRef.current = null;
    }
  }, []);

  // Data fetching
  const { data, isLoading, isFetching, totalCount } = useGetDocuments({
    projectId,
    serviceId,
    serviceType,
    page,
    pageSize: PAGE_SIZE,
  });

  // Clamp page if total shrinks (e.g. after deletion)
  useEffect(() => {
    if (isFetching) return;
    const maxPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    if (page > maxPage) setPage(maxPage);
  }, [isFetching, totalCount, page]);

  // Processed data
  const results = useMemo(() => data || [], [data]);

  const value: DocumentsContextValue = useMemo(
    () => ({
      // Data
      results,
      isLoading,
      projectId,
      serviceId,
      serviceType,
      isBidReview,

      // Pagination
      page,
      setPage,
      totalCount,
      pageSize: PAGE_SIZE,
      isFetching,

      // Modal state
      isModalOpen,
      blobToDelete,
      findingsToDelete,
      isEditModalOpen,
      itemToEdit: blobToEdit as Blob | ConsigliFile | null,
      newBaseName,
      isDeleting,
      isUpdating,

      // Detail view state
      selectedItem,
      isDetailViewOpen,

      // File preview state
      previewItem,
      setPreviewItem,

      // Modal handlers
      handleCloseModal,
      handleCloseEditModal,
      handleConfirmDelete,
      handleConfirmEdit,
      setNewBaseName,
      setIsDetailViewOpen,
      setSelectedItem,

      // Action handlers
      handleViewDetails: handleViewDetailsClick as (item: unknown) => void,
      handleEditFilename: handleEditFilenameClick as (item: unknown) => void,
      handleDownload: handleDownload as (item: unknown) => void,
      handleDelete: handleDeleteClick as (item: unknown) => void,
    }),
    [
      results,
      isLoading,
      isFetching,
      totalCount,
      page,
      projectId,
      serviceId,
      serviceType,
      isBidReview,
      isModalOpen,
      blobToDelete,
      findingsToDelete,
      isEditModalOpen,
      blobToEdit,
      newBaseName,
      isDeleting,
      isUpdating,
      selectedItem,
      isDetailViewOpen,
      previewItem,
      handleCloseModal,
      handleCloseEditModal,
      handleConfirmDelete,
      handleConfirmEdit,
      setNewBaseName,
      setIsDetailViewOpen,
      setSelectedItem,
      handleViewDetailsClick,
      handleEditFilenameClick,
      handleDownload,
      handleDeleteClick,
    ],
  );

  return <DocumentsContext.Provider value={value}>{children}</DocumentsContext.Provider>;
};
